import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogContent,
  Typography,
  CircularProgress,
  Box,
  Button,
} from "@mui/material";
import { AppDispatch } from "../../../redux/store/store";
import { getIdentityVerificationSession } from "../../../redux/actions/circleAction";
import {
  selectIdentityIsLoading,
  selectIdentityError,
  selectIdentitySessionUrl,
  selectIdentityIsProcessing,
  selectIdentityAlreadyVerified,
} from "../../../redux/selectors/accountVerification";

interface Props {
  open: boolean;
  onClose: () => void;
  userId: string;
  onVerified: () => any;
}

export default function IdentityVerificationModal({
  open,
  onClose,
  userId,
  onVerified,
}: Props) {
  const dispatch = useDispatch<AppDispatch>();
  const sessionUrl = useSelector(selectIdentitySessionUrl);
  const loading = useSelector(selectIdentityIsLoading);
  const error = useSelector(selectIdentityError);
  const processing = useSelector(selectIdentityIsProcessing);
  const alreadyVerified = useSelector(selectIdentityAlreadyVerified);

  // as soon as modal opens up we ask server for session
  useEffect(() => {
    if (open) {
      // const userId = userData?._id?.toString() ?? "";
      dispatch(getIdentityVerificationSession({ userId }));
    }
  }, [open, dispatch, userId]);

  useEffect(() => {
    if (open && alreadyVerified) {
      onVerified();
    }
  }, [open, alreadyVerified, onVerified]);

  // once we get the session url, instantly redirected into stripe hosted flow
  useEffect(() => {
    // A processing or already-verified session must not send the user back to Stripe to re-submit.
    if (sessionUrl && !processing && !alreadyVerified) {
      window.location.href = sessionUrl;
    }
  }, [sessionUrl, processing, alreadyVerified]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogContent sx={{ textAlign: "center", py: 4 }}>
        <Typography variant="h6" gutterBottom>
          {processing ? "Verification In Progress" : "Verify Your Identity"}
        </Typography>
        <Typography color="text.secondary" gutterBottom>
          {processing
            ? "We already received your documents. Stripe is still reviewing them, so there is nothing more to submit right now."
            : "We’ll open Stripe’s secure flow to confirm your documents."}
        </Typography>

        {error && (
          <Typography color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}

        {loading && (
          <Box sx={{ mt: 3 }}>
            <CircularProgress />
            <Typography sx={{ mt: 1 }}>Preparing verification…</Typography>
          </Box>
        )}

        {processing && !loading && (
          <Box sx={{ mt: 3 }}>
            <Button variant="outlined" onClick={onClose}>
              Close
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
